import { databricksIdentity, type DatabricksPrincipal } from "./identity.js";

export interface DatabricksAuthenticatedFetchOptions {
  principal: DatabricksPrincipal;
  fetchImpl?: typeof fetch;
  additionalHeaders?:
    | ConstructorParameters<typeof Headers>[0]
    | (() =>
        | ConstructorParameters<typeof Headers>[0]
        | Promise<ConstructorParameters<typeof Headers>[0]>);
}

/**
 * Fetch adapter for third-party SDKs that require a standard fetch surface.
 * A fresh principal token is resolved per attempt and one auth refresh retry is bounded to 401/403.
 */
export function createDatabricksAuthenticatedFetch(
  options: DatabricksAuthenticatedFetchOptions,
): typeof fetch {
  let token = databricksIdentity(options.principal);
  const fetchImpl = options.fetchImpl ?? fetch;
  return async (input: string | URL | Request, init?: RequestInit): Promise<Response> => {
    const request = async (forceRefresh: boolean): Promise<Response> => {
      if (forceRefresh) token = databricksIdentity(options.principal);
      const inherited = input instanceof Request ? input.headers : undefined;
      const headers = new Headers(inherited);
      for (const [key, value] of new Headers(init?.headers)) headers.set(key, value);
      const additional =
        typeof options.additionalHeaders === "function"
          ? await options.additionalHeaders()
          : options.additionalHeaders;
      for (const [key, value] of new Headers(additional)) headers.set(key, value);
      const accessToken = await token();
      headers.set("authorization", `Bearer ${accessToken}`);
      headers.set("x-api-key", accessToken);
      return fetchImpl(input, { ...init, headers });
    };
    const response = await request(false);
    return response.status === 401 || response.status === 403 ? request(true) : response;
  };
}

export function databricksAnthropicGatewayBaseUrl(host: string): string {
  const normalized = new URL(/^https?:\/\//i.test(host) ? host : `https://${host}`);
  return `${normalized.origin}/ai-gateway/anthropic/v1`;
}
